import tagService from '../services/tags'
import { createSlice } from '@reduxjs/toolkit'

import { setSuccessNotification, setErrorNotification } from './notificationReducer'

// Store tags keyed by tagType, e.g. { default: [...], categories: [...] }
const initialState = {}

const tagSlice = createSlice({
    name: 'tags',
    initialState,
    reducers: {
        setTags(state, action) {
            const { tagType, tags } = action.payload
            state[tagType] = tags
        },
        clearTags(state, action) {
            const { tagType } = action.payload
            delete state[tagType]
        },
        // A fetch for this type failed. null (not a deleted key) keeps every
        // `=== undefined` "not yet requested" guard closed, so consumers whose
        // effects re-run on each store change don't re-request forever (#384).
        // Only retryTags reopens it.
        setTagsFailed(state, action) {
            const { tagType } = action.payload
            state[tagType] = null
        },
        appendTag(state, action){
            const { tagType, tag } = action.payload
            if (!state[tagType]) state[tagType] = []
            state[tagType].push(tag)
        },
        destroyTag(state, action){
            const { tagType, id } = action.payload
            if (!state[tagType]) return
            state[tagType] = state[tagType].filter(tag => tag.id !== id)
        },
        changeTag(state, action){
            const { tagType, tag } = action.payload
            if (!state[tagType]) return
            state[tagType] = state[tagType].map(t => t.id === tag.id ? tag : t)
        }
    }
})

export const { setTags, clearTags, setTagsFailed, appendTag, destroyTag, changeTag } = tagSlice.actions

// The server derives `slug` and validates `name` (issue #374); surface its
// validation message instead of the generic toast when there is one.
const nameError = (e) => e?.response?.data?.name?.[0]

export const createTag = (data, admin, tagType = 'default') => {
    return async dispatch => {
        if(admin) {
            try {
                const tag = await tagService.create(tagType, data)
                // Prefer tag's own type if returned by the API
                const resolvedType = tag.tagType || tag.type || tagType
                dispatch(appendTag({ tagType: resolvedType, tag }))
                dispatch(setSuccessNotification('Tag created successfully'))
            } catch (e) {
                dispatch(setErrorNotification(nameError(e) || 'Error creating tag, if persists please notify developer team'))
            }
        } else {
            dispatch(setErrorNotification('Permission Denied: You must be a staff to perform this action'))
        }
    }
}


export const initializeTags = (tagType = 'default') => {
    return async (dispatch, getState) => {
        // If this tagType has already been initialized (even as an empty array), is in
        // flight, or previously failed (marked null), don't fetch again.
        const existing = getState().tags?.[tagType]
        if (existing !== undefined) {
            return
        }

        // Mark as initialized with an empty array to avoid duplicate concurrent fetches.
        dispatch(setTags({ tagType, tags: [] }))

        try {
            const tags = await tagService.retrieve(tagType)
            dispatch(setTags({ tagType, tags: tags }))
        } catch {
            // Leave a failed marker rather than deleting the key: deleting it re-opened
            // the guard above, and every consumer effect keyed on state.tags then
            // re-dispatched immediately -- an unbounded request + toast loop (#384).
            dispatch(setTagsFailed({ tagType }))
            dispatch(setErrorNotification('Error retrieving tags'))
        }
    }
}

// Explicit, user-initiated retry of a failed tag fetch (#384).
export const retryTags = (tagType = 'default') => async (dispatch) => {
    dispatch(clearTags({ tagType }))
    await dispatch(initializeTags(tagType))
}

export const deleteTag = (id, admin, tagType = 'default') => {
    return async dispatch => {
        if(admin) {
            try {
                await tagService.destroy(tagType, id)
                dispatch(destroyTag({ tagType, id }))
                dispatch(setSuccessNotification('Tag deleted successfully'))
            } catch {
                dispatch(setErrorNotification('Error deleting tag'))
            }
        } else {
            dispatch(setErrorNotification('Permission Denied: You must be a staff to perform this action'))
        }
    }
}


export const updateTag = (id, data, admin, tagType = 'default') => {
    return async dispatch => {
        if(admin) {
            try {
                const tag = await tagService.update(tagType, id, data)
                const resolvedType = tag.tagType || tag.type || tagType
                dispatch(changeTag({ tagType: resolvedType, tag }))
                dispatch(setSuccessNotification('Tag updated successfully'))
            } catch (e) {
                dispatch(setErrorNotification(nameError(e) || 'Update of tag failed'))
            }
        } else {
            dispatch(setErrorNotification('Permission Denied: You must be a staff to perform this action'))
        }
    }
}


export default tagSlice.reducer

